import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { environment } from '../../../environment/environment';
import { Location } from '../../../models/location/Location.model';
import { Room } from '../../../models/location/Room.model';
import { DUNA_LOCATIONS_ADMIN_EVENT } from '../../../services/component-data-source/data/locations/locations-admin-events';
import { LocationRoomSurveyDialogComponent } from './location-room-survey-dialog.component';
import { ConfirmDeleteDialogComponent } from './confirm-delete-dialog.component';
import { SuggestedLocationsDialogComponent } from './suggested-locations-dialog.component';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';

const FORM_SUCCESS = 'duna-locations-admin-form-success';

@Component({
  selector: 'app-duna-locations-and-rooms',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-locations-and-rooms.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-locations-and-rooms.component.scss',
})
export class DunaLocationsAndRoomsComponent implements OnInit, OnDestroy {
  @ViewChild('locationsTable') locationsTable?: DynamicTableComponent;
  @ViewChild('roomsTable') roomsTable?: DynamicTableComponent;

  /** Antal foreslåede lokationer (fra API totalCount); bruges til at vise knappen kun når > 0. */
  suggestedCount = 0;

  private formDialogRef: ReturnType<MatDialog['open']> | null = null;
  private suggestedDialogRef: MatDialogRef<SuggestedLocationsDialogComponent> | null =
    null;

  readonly locationsDynamicInput = {
    dataSource: { type: 'text', value: 'locations_admin' },
  };
  readonly roomsDynamicInput = {
    dataSource: { type: 'text', value: 'rooms_admin' },
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private componentDataDestService: ComponentDataDestService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    window.addEventListener(
      DUNA_LOCATIONS_ADMIN_EVENT,
      this.onAdminTableEvent as EventListener
    );
    window.addEventListener(FORM_SUCCESS, this.onFormSuccess as EventListener);
    this.loadSuggestedCount();
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      DUNA_LOCATIONS_ADMIN_EVENT,
      this.onAdminTableEvent as EventListener
    );
    window.removeEventListener(
      FORM_SUCCESS,
      this.onFormSuccess as EventListener
    );
  }

  private onFormSuccess = (): void => {
    if (this.formDialogRef) {
      this.formDialogRef.close();
      this.formDialogRef = null;
    }
    this.refreshAllTables();
    this.loadSuggestedCount();
  };

  private onAdminTableEvent = (ev: Event): void => {
    const e = ev as CustomEvent;
    const d = e.detail as {
      entity: 'suggested' | 'location' | 'room';
      action: 'open-create' | 'open-edit' | 'confirm-delete';
      row?: Location | Room;
      locationGuid?: string;
    };
    if (!d?.entity || !d?.action) return;

    if (d.entity === 'suggested' && d.action === 'open-create' && d.row) {
      void this.openLocationForm('create', undefined, d.row as Location);
      return;
    }
    if (d.entity === 'location') {
      if (d.action === 'open-create') {
        void this.openLocationForm('create');
        return;
      }
      if (d.action === 'open-edit' && d.row) {
        void this.openLocationForm('edit', d.row as Location);
        return;
      }
      if (d.action === 'confirm-delete' && d.row) {
        this.confirmDeleteLocation(d.row as Location);
        return;
      }
    }
    if (d.entity === 'room') {
      if (d.action === 'open-create') {
        void this.openRoomForm('create', undefined, d.locationGuid);
        return;
      }
      if (d.action === 'open-edit' && d.row) {
        void this.openRoomForm('edit', d.row as Room);
        return;
      }
      if (d.action === 'confirm-delete' && d.row) {
        this.confirmDeleteRoom(d.row as Room);
        return;
      }
    }
  };

  newLocation(): void {
    void this.openLocationForm('create');
  }

  newRoom(): void {
    void this.openRoomForm('create');
  }

  openSuggestedModal(): void {
    if (this.suggestedDialogRef) {
      return;
    }
    this.suggestedDialogRef = this.dialog.open(
      SuggestedLocationsDialogComponent,
      {
        width: '90vw',
        minWidth: '960px',
        maxWidth: '1400px',
        maxHeight: '92vh',
        autoFocus: false,
      }
    );
    this.suggestedDialogRef.afterClosed().subscribe(() => {
      this.suggestedDialogRef = null;
      this.loadSuggestedCount();
    });
  }

  /** Henter totalCount med minimal side – kun til at vise om knappen skal vises. */
  loadSuggestedCount(): void {
    this.http
      .get<{ totalCount?: number }>(
        `${environment.apiUrl}/locations/suggested`,
        {
          params: { pageSize: '1', pageIndex: '0' },
        }
      )
      .subscribe({
        next: (r) => {
          this.suggestedCount = r?.totalCount ?? 0;
          this.cdr.markForCheck();
        },
        error: () => {
          this.suggestedCount = 0;
          this.cdr.markForCheck();
        },
      });
  }

  private async openLocationForm(
    mode: 'create' | 'edit',
    existing?: Location,
    suggested?: Location
  ): Promise<void> {
    try {
      const base = (await firstValueFrom(
        this.http.get<Record<string, unknown>>('assets/forms/locations.json')
      )) as object;
      let receive = 'add_location';
      let data: Record<string, unknown> = {};

      if (mode === 'edit' && existing) {
        receive = 'update_location';
        const full = await firstValueFrom(
          this.http.get<{
            guid: string;
            locations_name: string;
            clean_name: string;
            latitude?: number | null;
            longitude?: number | null;
            data?: unknown;
          }>(`${environment.apiUrl}/locations/${existing.guid}`)
        );
        const raw = full.data;
        let addr = '';
        if (raw && typeof raw === 'object') {
          addr =
            (raw as { locations_address?: string }).locations_address ||
            (raw as { address?: string }).address ||
            '';
        } else if (typeof raw === 'string') {
          addr = raw;
        }
        data = {
          guid: full.guid,
          locations_name: full.locations_name,
          clean_name: full.clean_name,
          locations_address: addr,
          latitude: full.latitude ?? '',
          longitude: full.longitude ?? '',
        };
      } else if (suggested) {
        data = {
          locations_name: suggested.locations_name,
          clean_name: suggested.clean_name,
          locations_address: '',
          latitude: '',
          longitude: '',
          guid: '',
        };
      } else {
        data = {
          locations_name: '',
          clean_name: '',
          locations_address: '',
          latitude: '',
          longitude: '',
          guid: '',
        };
      }

      const merged = { ...base, data };
      const title =
        mode === 'edit'
          ? 'Rediger lokation'
          : suggested
          ? 'Opret lokation fra forslag'
          : 'Ny lokation';
      this.openSurveyDialog(title, merged, receive);
    } catch (err) {
      console.error(err);
      this.snackBar.open('Kunne ikke åbne formularen.', 'OK', {
        duration: 4000,
      });
    }
  }

  private async openRoomForm(
    mode: 'create' | 'edit',
    existing?: Room,
    defaultLocationGuid?: string
  ): Promise<void> {
    try {
      const base = (await firstValueFrom(
        this.http.get<Record<string, unknown>>(
          'assets/forms/locations_rooms.json'
        )
      )) as object;
      let receive = 'add_room';
      let data: Record<string, unknown>;

      if (mode === 'edit' && existing) {
        receive = 'update_room';
        data = {
          guid: existing.guid,
          room_name: existing.room_name,
          location_guid: existing.location_guid,
          capacity: existing.capacity,
          description: existing.description ?? '',
        };
      } else {
        data = {
          room_name: '',
          location_guid: defaultLocationGuid ?? '',
          capacity: 0,
          description: '',
          guid: '',
        };
      }

      const merged = { ...base, data };
      const title = mode === 'edit' ? 'Rediger lokale' : 'Nyt lokale';
      this.openSurveyDialog(title, merged, receive);
    } catch (err) {
      console.error(err);
      this.snackBar.open('Kunne ikke åbne formularen.', 'OK', {
        duration: 4000,
      });
    }
  }

  private openSurveyDialog(
    title: string,
    surveyJson: object,
    formReceive: string
  ): void {
    if (this.formDialogRef) {
      this.formDialogRef.close();
      this.formDialogRef = null;
    }
    this.formDialogRef = this.dialog.open(LocationRoomSurveyDialogComponent, {
      width: 'min(560px, 96vw)',
      maxHeight: '90vh',
      data: { title, surveyJson, formReceive },
    });
    this.formDialogRef.afterClosed().subscribe(() => {
      if (this.formDialogRef) {
        this.formDialogRef = null;
      }
    });
    this.cdr.markForCheck();
  }

  private confirmDeleteLocation(loc: Location): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: {
        title: 'Slet lokation?',
        message: `Er du sikker på, at du vil slette lokationen "${loc.locations_name}"? Alle lokaler under lokationen uden tilknyttede begivenheder slettes også.`,
      },
    });
    ref.afterClosed().subscribe((ok) => {
      if (ok) this.runDeleteLocation(loc.guid);
    });
  }

  private confirmDeleteRoom(room: Room): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: {
        title: 'Slet lokale?',
        message: `Er du sikker på, at du vil slette lokalet "${room.room_name}"?`,
      },
    });
    ref.afterClosed().subscribe((ok) => {
      if (ok) this.runDeleteRoom(room.guid);
    });
  }

  private runDeleteLocation(guid: string): void {
    const fn = this.componentDataDestService.getDataDest('delete_location');
    if (!fn) return;
    const obs = fn({ guid }) as import('rxjs').Observable<unknown> | undefined;
    if (obs && typeof obs.subscribe === 'function') {
      obs.subscribe({
        error: (err) => this.showHttpError(err),
      });
    }
  }

  private runDeleteRoom(guid: string): void {
    const fn = this.componentDataDestService.getDataDest('delete_room');
    if (!fn) return;
    const obs = fn({ guid }) as import('rxjs').Observable<unknown> | undefined;
    if (obs && typeof obs.subscribe === 'function') {
      obs.subscribe({
        error: (err) => this.showHttpError(err),
      });
    }
  }

  private showHttpError(err: unknown): void {
    let msg = 'Der opstod en fejl.';
    if (err && typeof err === 'object' && 'error' in err) {
      const e = err as { error?: { message?: string } | string };
      if (typeof e.error === 'object' && e.error?.message)
        msg = e.error.message;
      else if (typeof e.error === 'string') msg = e.error;
    }
    this.snackBar.open(msg, 'OK', { duration: 6000 });
  }

  private refreshAllTables(): void {
    setTimeout(() => {
      this.locationsTable?.refreshTable();
      this.roomsTable?.refreshTable();
      this.suggestedDialogRef?.componentInstance?.refreshTable();
      this.cdr.markForCheck();
    }, 0);
  }
}
